import { TariffWrapper } from 'context/tariff_state';
import { PurchaseOrder } from 'data/interfaces/Common';
import BannerRepository from 'data/repositories/BannerRepository';
import PurchaseRepository from 'data/repositories/PurchaseRepository';
import { redirectMap } from 'pages/redirect';
import { inter, montserrat } from 'styles/fonts';
import type { NotificationType } from 'types/notificationRedirect';
import classNames from 'classnames';
import Layout from 'components/layout';
import { CharacterWrapper } from 'context/character_state';
import { CvWrapper } from 'context/cv_state';
import { IndexPageWrapper } from 'context/index_page_state';
import { NotificationsWrapper } from 'context/notifications_state';
import { PatternWrapper } from 'context/pattern_state';
import { AppWrapper } from 'context/state';
import { UserWrapper } from 'context/user_state';
import { ProfileType } from 'data/interfaces/IRegistration';
import { IUser } from 'data/interfaces/IUser';
import UserRepository from 'data/repositories/UserRepository';
import Cookies from 'js-cookie';
import { hasLocale, NextIntlClientProvider } from 'next-intl';
import type { AppProps as NextAppProps } from 'next/app';
import type { NextComponentType, NextPageContext } from 'next';
import type { ReactElement } from 'react';
import App, { AppContext } from 'next/app';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { withRouter } from 'next/router';
// import 'normalize.css';
import { useEffect, useState } from 'react';
import { CookiesProvider } from 'react-cookie';
import { getSelectorsByUserAgent } from 'react-device-detect';
import 'styles/globals.scss';
import 'styles/themes.scss';
import 'styles/global.css';
import 'styles/antdReset.css';
import 'swiper/css/bundle';
import { AppTheme, CookiesType, InterfaceLanguage, Routes, SalesBanners } from 'types/enums';
import { routing } from 'i18n/routing';
import AntdConfigProvider from 'components/ui-new/AntdConfigProvider';
import { TranslationProvider } from 'utils/translate';

const BottomSheetContainer = dynamic(() => import('components/layout/BottomSheetContainer'), {
  loading: () => <p>Loading...</p>,
});

const ModalContainer = dynamic(() => import('components/layout/ModalContainer'), {
  loading: () => <p>Loading...</p>,
});

const Snackbar = dynamic(() => import('components/layout/Snackbar'), {
  loading: () => <p>Loading...</p>,
});

interface PageProps {
  isMobile: boolean;
  token: string;
  region: string;
  orders: PurchaseOrder[];
  user?: IUser;
  profileType?: ProfileType;
  webViewOnly?: boolean;
  theme?: AppTheme;
  NEXT_LOCALE: InterfaceLanguage;
  acceptLanguage?: InterfaceLanguage;
  locale?: InterfaceLanguage;
  isChangedLanguage?: boolean;
  messages: Record<string, string>;
}

export interface AppProps extends Omit<NextAppProps, 'Component'> {
  Component: NextComponentType<NextPageContext, any, any> & {
    getLayout?: (page: ReactElement) => ReactElement;
  };
  pageProps: PageProps;
}

function MyApp({ Component, pageProps, router }: AppProps) {
  const [clientVisible, setClientVisible] = useState(false);
  const [banners, setBanners] = useState([]);

  useEffect(() => {
    BannerRepository.getBanners(
      pageProps.profileType ?? ProfileType.Employee,
      pageProps.token,
      Object.values(SalesBanners),
      (Cookies.get(CookiesType.locale) as InterfaceLanguage) ?? InterfaceLanguage.Russian,
      Cookies.get(CookiesType.uuid),
    )
      .then((data) => {
        Cookies.remove(CookiesType.uuid);
        Cookies.set(CookiesType.uuid, data.uuid);
        setBanners(data.banners);
      })
      .catch((err: Error): null => {
        console.warn(err);
        return null;
      });
  }, [pageProps.acceptLanguage, pageProps.NEXT_LOCALE]);

  useEffect(() => {
    setClientVisible(true);

    if (pageProps.isMobile) {
      document.body.classList.add('mobile-ua');
      document.documentElement.className = 'mobile-ua';
    }

    const disableScrollRestoration = () => {
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'manual';
      }
    };

    disableScrollRestoration();

    return () => {};
  }, [pageProps.isMobile]);

  const getPropsForMainLanding = () => {
    if (
      (router.pathname !== '/' || pageProps.token) &&
      router.pathname !== '/success-payment' &&
      router.pathname !== '/failed-payment'
    )
      return {};

    return {
      contentStyles: { background: '#131313' },
      hideHeaderDirectly: true,
    };
  };

  const getLayout = Component.getLayout || ((page) => page);

  return (
    <NextIntlClientProvider
      locale={(router.query.locale as string) || routing.defaultLocale}
      messages={pageProps.messages}>
      <TranslationProvider>
        <AntdConfigProvider>
          <CookiesProvider cookies={(Component as any).universalCookies}>
            <NotificationsWrapper isAuthenticated={Boolean(pageProps.token)}>
              <AppWrapper isMobile={pageProps.isMobile} theme={pageProps.theme} banners={banners}>
                <UserWrapper
                  token={pageProps.token}
                  profileType={pageProps.profileType}
                  user={pageProps.user}>
                  <CvWrapper>
                    <PatternWrapper>
                      <IndexPageWrapper>
                        <CharacterWrapper>
                          <TariffWrapper orders={pageProps.orders}>
                            <Head>
                              <meta
                                name='viewport'
                                content='width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover'
                              />
                            </Head>
                            <main className={classNames(montserrat.className, inter.className)}>
                              <Layout
                                webView={pageProps.webViewOnly}
                                authorized={Boolean(pageProps.token)}
                                {...getPropsForMainLanding()}>
                                {getLayout(<Component {...pageProps} />)}
                              </Layout>
                            </main>

                            {clientVisible && <BottomSheetContainer />}
                            {clientVisible && <ModalContainer />}
                            {clientVisible && <Snackbar />}
                          </TariffWrapper>
                        </CharacterWrapper>
                      </IndexPageWrapper>
                    </PatternWrapper>
                  </CvWrapper>
                </UserWrapper>
              </AppWrapper>
            </NotificationsWrapper>
          </CookiesProvider>
        </AntdConfigProvider>
      </TranslationProvider>
    </NextIntlClientProvider>
  );
}

MyApp.getInitialProps = async (appContext: AppContext) => {
  const props = await App.getInitialProps(appContext);
  const ua = appContext.ctx.req ? appContext.ctx.req?.headers['user-agent'] : navigator.userAgent;

  const { ctx } = appContext;
  const { page, __nextNotFoundSrcPage, type, ...query } = ctx.query;

  // Handler for CookiesType.locale duplicates
  const isServer = ctx.req && ctx.res;
  const cookieSrc = isServer
    ? ctx.req.headers.cookie
    : typeof window !== 'undefined'
      ? document.cookie
      : '';

  if (cookieSrc) {
    const localeCookies = cookieSrc
      .split('; ')
      .filter((c) => c.startsWith(`${CookiesType.locale}=`));

    if (localeCookies.length > 1) {
      const value = isServer
        ? localeCookies[localeCookies.length - 1].split('=')[1]
        : Cookies.get(CookiesType.locale);

      isServer
        ? ctx.res.setHeader('Set-Cookie', `${CookiesType.locale}=; Path=/; Max-Age=0; SameSite=Lax`)
        : Cookies.remove(CookiesType.locale, { path: '/' });

      if (value) {
        isServer
          ? ctx.res.setHeader('Set-Cookie', `${CookiesType.locale}=${value}; Path=/; SameSite=Lax`)
          : Cookies.set(CookiesType.locale, value);
      }

      console.warn(
        `[${isServer ? 'Server' : 'Client'}] Removed ${localeCookies.length - 1} duplicate locale cookies`,
      );
    }
  }

  if (type && typeof type === 'string') {
    const redirect = redirectMap[type as NotificationType['type']];
    if (redirect) {
      const redirectUrl = redirect(query);
      if (ctx.res) {
        ctx.res.writeHead(302, { Location: redirectUrl });
        ctx.res.end();
      }
    }
  }

  if (ua) {
    const { isMobile, isTablet } = getSelectorsByUserAgent(ua);
    if (isTablet && typeof window !== 'undefined' && window.screen.width >= 992) {
      props.pageProps.isMobile = false;
    } else {
      props.pageProps.isMobile = isMobile;
    }
  } else {
    props.pageProps.isMobile = false;
  }

  props.pageProps.webViewOnly = appContext.ctx.query.webViewOnly;
  props.pageProps.region = appContext.ctx.query.region;

  let locale = appContext.ctx.locale || routing.defaultLocale;

  if (appContext.ctx?.req && (appContext.ctx.req as any).cookies) {
    const cookies = (appContext.ctx.req as any).cookies;
    const isChangedLanguage = cookies[CookiesType.isChangedLang] === 'true';

    if (isChangedLanguage && cookies[CookiesType.locale]) {
      locale = cookies[CookiesType.locale];
    }
  }

  if (!hasLocale(routing.locales, locale)) {
    locale = routing.defaultLocale;
  }

  props.pageProps.locale = locale;

  props.pageProps.messages = (await import(`../../locales/${locale}/common.json`)).default;

  if (appContext.ctx?.req && (appContext.ctx.req as any).cookies) {
    props.pageProps.theme = (appContext.ctx as any).req.cookies[CookiesType.theme];
    props.pageProps.token = (appContext.ctx as any).req.cookies[CookiesType.accessToken];
    props.pageProps.profileType = (appContext.ctx as any).req.cookies[CookiesType.profileType];

    if (props.pageProps.profileType == 'unknown' && ctx.res) {
      ctx.res.writeHead(302, { Location: Routes.AuthNext });
      ctx.res.end();
    }

    if (props.pageProps.token) {
      try {
        props.pageProps.user = await UserRepository.getCurrentUser(appContext.ctx);
      } catch (e) {
        console.warn(e);
      }

      if (props.pageProps.profileType) {
        try {
          props.pageProps.orders = await PurchaseRepository.getOrders(
            props.pageProps.profileType,
            props.pageProps.token,
          );
        } catch (e) {
          console.warn(e);
        }
      }
    }
  }

  return props;
};

export default withRouter(MyApp);
